import { useId, useRef, useState, type FormEvent } from "react";
import { createOrganizationBodySchema, renameOrganizationBodySchema, type CreateOrganizationResponse, type PlatformOrganization } from "@gioia365/contracts";
import { locales, type Locale } from "@gioia365/i18n";
import { ApiRequestError } from "../../api/http";
import { useCreateOrganization, useRenameOrganization } from "../../api/queries";
import { Button } from "../../design/components/Button";
import { Dialog } from "../../design/components/Dialog";
import { NameFields } from "../../design/components/NameFields";
import { Select } from "../../design/components/Select";
import { TextField } from "../../design/components/TextField";
import { messageForError } from "../../i18n/errors";
import { useT, type Translate } from "../../i18n/locale";
import { timeZoneOptions } from "../settings/time-zones";
import "./platform.css";

// Le finestre di «Aziende di Gioia365» (piano M1b Task 11): «Nuova azienda», «Gestisci» e la conferma di
// sospensione e chiusura. Ognuna nasce a ogni apertura (la pagina le monta soltanto quando servono), quindi i
// campi non ricordano la volta prima. Una finestra modale rende inerte la pagina, avvisi compresi (D16): ciò che
// si decide qui e fallisce si spiega qui; le azioni di stato chiudono la finestra e la pagina mostra l'avviso.

/** Fuso e lingua di partenza di un'azienda nuova: l'Italia, come i mock-up. */
const DEFAULT_TIME_ZONE = "Europe/Rome";
const DEFAULT_LOCALE: Locale = "it";

/**
 * L'indirizzo breve che il nome suggerisce: il nome tutto attaccato, in minuscolo, senza accenti, spazi, trattini né altri
 * segni (decisione del product owner del 3 ottobre 2026: «Academy Ambasciata della Gioia» → `academyambasciatadellagioia`).
 */
function slugFromName(name: string): string {
  return name
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "")
    .slice(0, 40);
}

/** I campi di cui il server dice che non vanno (`422 validation_failed`, `details.fields`). */
function invalidFields(error: unknown): string[] {
  if (!(error instanceof ApiRequestError) || error.code !== "validation_failed") return [];
  const fields = error.details?.fields;
  return Array.isArray(fields) ? fields.filter((field): field is string => typeof field === "string") : [];
}

/** I campi del modulo, nell'ordine in cui compaiono: il primo sbagliato prende il focus. */
const FIELD_ORDER = ["name", "slug", "ownerFirstName", "ownerLastName", "ownerEmail"] as const;
type InputField = (typeof FIELD_ORDER)[number];
type Field = InputField | "form";

/** Un problema del modulo, con il testo calcolato quando si mostra: segue la lingua dell'interfaccia. */
type Problem = { field: Field; text: (t: Translate) => string };

// Nome e cognome del titolare hanno un solo errore, letto sulla casella sbagliata.
const FIELD_TEXT: Record<InputField, "platform.fields.nameMissing" | "platform.fields.slugInvalid" | "platform.fields.ownerNameMissing" | "platform.fields.ownerEmailInvalid"> = {
  name: "platform.fields.nameMissing",
  slug: "platform.fields.slugInvalid",
  ownerFirstName: "platform.fields.ownerNameMissing",
  ownerLastName: "platform.fields.ownerNameMissing",
  ownerEmail: "platform.fields.ownerEmailInvalid",
};

function firstInvalid(fields: readonly string[]): InputField | undefined {
  return FIELD_ORDER.find((field) => fields.includes(field));
}

type CreateProps = {
  onClose: () => void;
  /** L'azienda è nata (o la preparazione ripresa): la pagina mostra il link del titolare o l'avviso. */
  onCreated: (response: CreateOrganizationResponse) => void;
  /** La preparazione si è fermata a metà (`provisioning_failed`): la pagina chiude la finestra e lo dice. */
  onStalled: () => void;
  /** Il nome dell'azienda mentre la richiesta corre, `null` quando finisce: la pagina spegne ciò che apre altre finestre. */
  onRunningChange: (name: string | null) => void;
};

/**
 * «Nuova azienda». La creazione può durare (prepara anche il database dell'azienda): il primario dice cosa sta
 * succedendo, non si preme due volte e la finestra non si chiude finché la richiesta non risponde. Se la
 * preparazione si ferma a metà l'azienda esiste già, con nome e titolare salvati alla prima chiamata (la ripresa
 * ignora ciò che il modulo dice di diverso): la finestra si chiude e la riga «in preparazione» della tabella ha il
 * suo pulsante per riprenderla.
 */
export function CreateOrganizationDialog({ onClose, onCreated, onStalled, onRunningChange }: CreateProps) {
  const t = useT();
  const formId = useId();
  const create = useCreateOrganization();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  // Finché l'indirizzo non è scritto a mano segue il nome; svuotato del tutto, torna a seguirlo.
  const [slugEdited, setSlugEdited] = useState(false);
  const [timeZone, setTimeZone] = useState(DEFAULT_TIME_ZONE);
  const [locale, setLocale] = useState<Locale>(DEFAULT_LOCALE);
  const [ownerFirstName, setOwnerFirstName] = useState("");
  const [ownerLastName, setOwnerLastName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [problem, setProblem] = useState<Problem | null>(null);
  const refs = {
    name: useRef<HTMLInputElement>(null),
    slug: useRef<HTMLInputElement>(null),
    ownerFirstName: useRef<HTMLInputElement>(null),
    ownerLastName: useRef<HTMLInputElement>(null),
    ownerEmail: useRef<HTMLInputElement>(null),
    form: useRef<HTMLButtonElement>(null),
  };
  // Sincrono, a differenza di `create.isPending`: due clic ravvicinati non mandano due richieste.
  const running = useRef(false);
  const busy = create.isPending;

  function fail(field: Field, text: (tr: Translate) => string) {
    setProblem({ field, text });
    refs[field].current?.focus();
  }

  function handleName(value: string) {
    setName(value);
    if (!slugEdited) setSlug(slugFromName(value));
  }

  function handleSlug(value: string) {
    // Le maiuscole non servono e non valgono: diventano minuscole mentre si scrive.
    setSlug(value.toLowerCase());
    setSlugEdited(value !== "");
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (running.current) return;
    // Lo schema dei contratti è lo stesso del server: ciò che passa qui passa anche là.
    const parsed = createOrganizationBodySchema.safeParse({
      name,
      slug,
      timeZone,
      defaultLocale: locale,
      ownerEmail: ownerEmail.trim(),
      ownerFirstName,
      ownerLastName,
    });
    if (!parsed.success) {
      const invalid = firstInvalid(parsed.error.issues.map((issue) => String(issue.path[0])));
      if (invalid) fail(invalid, (tr) => tr(FIELD_TEXT[invalid]));
      else fail("form", (tr) => tr("errors.validation_failed"));
      return;
    }
    setProblem(null);
    running.current = true;
    onRunningChange(parsed.data.name);
    try {
      onCreated(await create.mutateAsync(parsed.data));
    } catch (error) {
      const invalid = firstInvalid(invalidFields(error));
      if (error instanceof ApiRequestError && error.code === "provisioning_failed") onStalled();
      else if (error instanceof ApiRequestError && error.code === "slug_taken") fail("slug", (tr) => messageForError(tr, error));
      else if (invalid) fail(invalid, (tr) => tr(FIELD_TEXT[invalid]));
      else fail("form", (tr) => messageForError(tr, error));
    } finally {
      running.current = false;
      onRunningChange(null);
    }
  }

  const errorOf = (field: Field) => (problem?.field === field ? problem.text(t) : undefined);
  const formError = errorOf("form");
  const languages = locales.map((value) => ({ value, label: t(value === "it" ? "account.language.italian" : "account.language.english") }));

  return (
    <Dialog
      open
      title={t("platform.new")}
      onClose={() => {
        if (!busy) onClose();
      }}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>{t("common.cancel")}</Button>
          <Button ref={refs.form} variant="primary" type="submit" form={formId} disabled={busy} aria-busy={busy ? "true" : undefined}>
            {busy ? t("platform.create.submitting") : t("platform.create.submit")}
          </Button>
        </>
      }
    >
      <form id={formId} className="g-stack" onSubmit={(event) => void handleSubmit(event)} aria-busy={busy} noValidate>
        <TextField
          id="platform-create-name"
          ref={refs.name}
          label={t("platform.fields.name")}
          value={name}
          maxLength={120}
          autoComplete="off"
          error={errorOf("name")}
          onChange={(event) => handleName(event.target.value)}
        />
        <TextField
          id="platform-create-slug"
          ref={refs.slug}
          label={t("platform.fields.slug")}
          value={slug}
          maxLength={40}
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          hint={t("platform.fields.slugHint")}
          error={errorOf("slug")}
          onChange={(event) => handleSlug(event.target.value)}
        />
        <Select id="platform-create-zone" label={t("platform.fields.timeZone")} value={timeZone} onChange={setTimeZone} options={timeZoneOptions(t, DEFAULT_TIME_ZONE)} />
        <Select
          id="platform-create-language"
          label={t("platform.fields.language")}
          value={locale}
          onChange={(value) => {
            const next = locales.find((candidate) => candidate === value);
            if (next) setLocale(next);
          }}
          options={languages}
        />
        <NameFields
          id="platform-create-owner-name"
          firstName={{ label: t("platform.fields.ownerFirstName"), value: ownerFirstName, onChange: setOwnerFirstName, ref: refs.ownerFirstName }}
          lastName={{ label: t("platform.fields.ownerLastName"), value: ownerLastName, onChange: setOwnerLastName, ref: refs.ownerLastName }}
          error={errorOf("ownerFirstName") ?? errorOf("ownerLastName")}
          invalid={problem?.field === "ownerLastName" ? "lastName" : "firstName"}
        />
        <TextField
          id="platform-create-owner-email"
          ref={refs.ownerEmail}
          label={t("platform.fields.ownerEmail")}
          type="email"
          value={ownerEmail}
          maxLength={254}
          autoComplete="off"
          hint={t("platform.fields.ownerEmailHint")}
          error={errorOf("ownerEmail")}
          onChange={(event) => setOwnerEmail(event.target.value)}
        />
        {formError ? (
          <p className="g-field__error" role="alert">
            {formError}
          </p>
        ) : null}
        {/* Sempre nella pagina e vuota finché non serve: uno screen reader annuncia soltanto ciò che vi compare. */}
        <p className="g-platform__wait" role="status">
          {busy ? t("platform.create.waiting") : null}
        </p>
      </form>
    </Dialog>
  );
}

type ManageProps = {
  organization: PlatformOrganization;
  onClose: () => void;
  onStatus: (action: "suspend" | "resume" | "close") => void;
};

/** «Gestisci» un'azienda: i suoi dati e le azioni di stato. Sospendere e chiudere chiedono conferma. */
export function ManageOrganizationDialog({ organization, onClose, onStatus }: ManageProps) {
  const t = useT();
  const { name, status } = organization;
  return (
    <Dialog
      open
      size="wide"
      title={name}
      onClose={onClose}
      footer={
        <>
          {status === "active" ? (
            <Button variant="ghost" onClick={() => onStatus("suspend")}>{t("platform.manage.suspend")}</Button>
          ) : null}
          {status === "suspended" ? (
            <Button variant="ghost" onClick={() => onStatus("resume")}>{t("platform.manage.resume")}</Button>
          ) : null}
          {/* Il nome dell'azienda nel nome accessibile: «Chiudi» da solo si confonderebbe con la «X» della finestra. */}
          {status === "active" || status === "suspended" ? (
            <Button variant="ghost" onClick={() => onStatus("close")} aria-label={t("platform.manage.closeNamed", { organization: name })}>
              {t("platform.manage.close")}
            </Button>
          ) : null}
          <span className="g-platform__spacer" />
          <Button onClick={onClose}>{t("platform.manage.done")}</Button>
        </>
      }
    >
      <div className="g-stack">
        {status !== "closed" ? <RenameOrganizationForm organization={organization} /> : null}
        <TextField id="platform-manage-slug" label={t("platform.fields.slug")} value={organization.slug} readOnly hint={t("platform.fields.slugHint")} />
        <TextField
          id="platform-manage-owner"
          label={t("platform.fields.owner")}
          value={t("platform.fields.ownerValue", { name: organization.ownerName, email: organization.ownerEmail })}
          readOnly
        />
      </div>
    </Dialog>
  );
}

/**
 * «Cambia nome» (decisione del product owner del 4 ottobre 2026): il nome dell'azienda, con le stesse regole della
 * creazione. L'indirizzo breve sotto non cambia. «Salva il nome» resta spento finché il nome non è cambiato.
 */
function RenameOrganizationForm({ organization }: { organization: PlatformOrganization }) {
  const t = useT();
  const rename = useRenameOrganization();
  const [name, setName] = useState(organization.name);
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const changed = name.trim() !== organization.name;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const parsed = renameOrganizationBodySchema.safeParse({ name });
    if (!parsed.success) {
      setProblem(t("platform.fields.nameMissing"));
      return;
    }
    setProblem(null);
    setSaved(false);
    try {
      await rename.mutateAsync({ organizationId: organization.id, name: parsed.data.name });
      setName(parsed.data.name);
      setSaved(true);
    } catch (error) {
      setProblem(messageForError(t, error));
    }
  }

  return (
    <form className="g-stack" onSubmit={(event) => void handleSubmit(event)} noValidate>
      <TextField
        id="platform-manage-name"
        label={t("platform.fields.name")}
        value={name}
        maxLength={120}
        onChange={(event) => {
          setName(event.target.value);
          setSaved(false);
        }}
        hint={t("platform.manage.renameHint")}
        error={problem ?? undefined}
      />
      <div className="g-platform__actions">
        <Button type="submit" disabled={!changed || rename.isPending} aria-busy={rename.isPending ? "true" : undefined}>
          {rename.isPending ? t("platform.manage.renaming") : t("platform.manage.rename")}
        </Button>
        {saved ? <span role="status">{t("platform.manage.renamed")}</span> : null}
      </div>
    </form>
  );
}

type ConfirmProps = {
  action: "suspend" | "close";
  organization: string;
  onCancel: () => void;
  onConfirm: () => void;
};

/** La conferma prima di sospendere o chiudere; riattivare agisce subito. */
export function ConfirmTransitionDialog({ action, organization, onCancel, onConfirm }: ConfirmProps) {
  const t = useT();
  const suspend = action === "suspend";
  return (
    <Dialog
      open
      title={suspend ? t("platform.confirm.suspendTitle", { organization }) : t("platform.confirm.closeTitle", { organization })}
      onClose={onCancel}
      footer={
        <>
          <Button onClick={onCancel}>{t("common.cancel")}</Button>
          {suspend ? (
            <Button variant="primary" onClick={onConfirm}>{t("platform.manage.suspend")}</Button>
          ) : (
            <Button variant="primary" onClick={onConfirm} aria-label={t("platform.manage.closeNamed", { organization })}>
              {t("platform.manage.close")}
            </Button>
          )}
        </>
      }
    >
      <p>{suspend ? t("platform.confirm.suspendText") : t("platform.confirm.closeText")}</p>
    </Dialog>
  );
}
